import { useTranslation } from 'react-i18next';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { Badge, Card, Page, PageContent, Skeleton } from '@bliks-io/ui';
import { useSupportTickets } from '@/api/queries/useSupportTickets';
import { Link } from '@tanstack/react-router';
import { useAuthStore } from '@/stores/auth-store';
import { MessageSquare, ArrowLeft, AlertTriangle, type LucideIcon } from 'lucide-react';
import type { SupportTicketListItem } from '@bliks-io/contracts';
import { formatDate } from '@/lib/formatters';
import { getTicketStatusBadgeVariant, isTicketClosed } from './ticketStatus';

export function SupportTicketsPage() {
  const { t } = useTranslation();
  const slug = useAuthStore().slug();
  useDocumentTitle('pageTitles.supportTickets');

  return (
    <Page>
      <PageContent>
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <Link
              to="/$slug/help"
              params={{ slug }}
              className="text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-5 w-5" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{t('supportTickets.title')}</h1>
              <p className="text-sm text-muted-foreground">{t('supportTickets.subtitle')}</p>
            </div>
          </div>
        </div>

        <TicketsBody />
      </PageContent>
    </Page>
  );
}

function TicketsBody() {
  const { t } = useTranslation();
  const { data, isLoading, isError } = useSupportTickets();
  const tickets = data?.tickets || [];

  if (isLoading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  // A background refetch failure keeps the cached list; only a failure with no
  // data to show surfaces the error card.
  if (isError && tickets.length === 0) {
    return (
      <StatusCard
        icon={AlertTriangle}
        title={t('supportTickets.loadErrorTitle')}
        description={t('supportTickets.loadError')}
      />
    );
  }

  if (tickets.length === 0) {
    return (
      <StatusCard
        icon={MessageSquare}
        title={t('supportTickets.empty')}
        description={t('supportTickets.emptyDescription')}
      />
    );
  }

  const openTickets = tickets.filter((ticket) => !isTicketClosed(ticket.status));
  const closedTickets = tickets.filter((ticket) => isTicketClosed(ticket.status));

  return (
    <div className="space-y-6">
      {openTickets.length > 0 && (
        <TicketSection title={t('supportTickets.open')} tickets={openTickets} />
      )}
      {closedTickets.length > 0 && (
        <TicketSection title={t('supportTickets.closed')} tickets={closedTickets} />
      )}
    </div>
  );
}

function StatusCard({
  icon: Icon,
  title,
  description
}: {
  icon: LucideIcon;
  title: string;
  description: string;
}) {
  return (
    <Card className="p-8 text-center border-dashed">
      <Icon className="w-10 h-10 text-muted-foreground mx-auto mb-3" />
      <p className="text-muted-foreground font-medium mb-1">{title}</p>
      <p className="text-sm text-muted-foreground">{description}</p>
    </Card>
  );
}

function TicketSection({ title, tickets }: { title: string; tickets: SupportTicketListItem[] }) {
  const { t, i18n } = useTranslation();
  const slug = useAuthStore().slug();

  // Static keys (not a dynamic t(`...${status}`) template) so check:translations
  // can resolve every status label; the keys mirror SupportTicketStatusSchema.
  const statusLabels: Record<SupportTicketListItem['status'], string> = {
    received: t('supportTickets.status.received'),
    in_progress: t('supportTickets.status.inProgress'),
    resolved: t('supportTickets.status.resolved'),
    merged: t('supportTickets.status.merged')
  };

  return (
    <div>
      <h2 className="text-sm font-medium text-muted-foreground mb-2 uppercase tracking-wider">
        {title} ({tickets.length})
      </h2>
      <div className="space-y-2">
        {tickets.map((ticket) => (
          <Card key={ticket.id} className="p-4 transition-colors hover:bg-accent/50">
            <Link
              to="/$slug/help/tickets/$ticketId"
              params={{ slug, ticketId: ticket.id }}
              className="flex items-center justify-between"
            >
              <div className="flex items-center gap-3 min-w-0">
                <MessageSquare className="h-4 w-4 text-muted-foreground shrink-0" />
                <div className="min-w-0">
                  <p className="font-medium truncate">{ticket.title}</p>
                  <p className="text-xs text-muted-foreground">#{ticket.number}</p>
                </div>
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {ticket.hasNewReply && (
                  <Badge variant="destructive">{t('supportTickets.newReply')}</Badge>
                )}
                <Badge variant={getTicketStatusBadgeVariant(ticket.status)}>
                  {statusLabels[ticket.status]}
                </Badge>
                <span className="text-xs text-muted-foreground">
                  {formatDate(ticket.updatedAt, i18n.language)}
                </span>
              </div>
            </Link>
          </Card>
        ))}
      </div>
    </div>
  );
}
